import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCents } from '../lib/money';
import StatusBadge from '../components/StatusBadge.jsx';

// Zip mirrors the backend's own fallback in orderService.SORTABLE_COLUMNS:
// service_address (where the tech goes) first, billing_address otherwise.
function zipOf(order) {
  return order.service_address?.postalCode || order.billing_address?.postalCode || null;
}

// Clickable column headers (Dan/CSR feedback, 2026-09-21): `sortKey` matches
// a key in the backend's SORTABLE_COLUMNS allowlist (orderService.js) --
// omit it on a column that isn't sortable (HCP link).
function SortableHeader({ label, sortKey, sortBy, sortDir, onSort }) {
  if (!sortKey) return <th>{label}</th>;
  const active = sortBy === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      title={`Sort by ${label}`}
    >
      {label} {active ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </th>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sortBy, setSortBy] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function load({ sortBy: sb = sortBy, sortDir: sd = sortDir } = {}) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      params.set('sortBy', sb);
      params.set('sortDir', sd);
      const { orders } = await api.get(`/orders?${params.toString()}`);
      setOrders(orders);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clicking the currently-active column flips direction; clicking a new
  // column switches to it, defaulting to ascending (descending for a fresh
  // click on "Created", since "newest first" is what a CSR expects there).
  function handleSort(key) {
    const nextDir = sortBy === key ? (sortDir === 'asc' ? 'desc' : 'asc') : key === 'created' ? 'desc' : 'asc';
    setSortBy(key);
    setSortDir(nextDir);
    load({ sortBy: key, sortDir: nextDir });
  }

  return (
    <div>
      <h2>Current Plans</h2>
      {error && <div className="error-banner">{error}</div>}

      <div className="card">
        <div className="form-row" style={{ alignItems: 'flex-end' }}>
          <div>
            <label>Search (customer / plan ID / plan name)</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
          </div>
          <div>
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="past_due">Past due</option>
              <option value="paused">Paused</option>
              <option value="canceled">Canceled</option>
            </select>
          </div>
          <div style={{ flex: '0 0 auto', marginBottom: 12 }}>
            <button className="btn btn-primary" onClick={() => load()}>
              Search
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        {loading ? (
          <p>Loading…</p>
        ) : orders.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No plans found.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <SortableHeader label="Customer" sortKey="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Zip" sortKey="zip" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Plan" sortKey="plan" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Monthly" sortKey="monthly" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Status" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Origin" sortKey="origin" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Rep" sortKey="rep" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Created" sortKey="created" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="HCP" />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{o.customer_name || '—'}</td>
                  <td>{zipOf(o) || '—'}</td>
                  <td>{o.plan_tier}</td>
                  <td>{formatCents(o.monthly_price_cents)}</td>
                  <td><StatusBadge status={o.status} /></td>
                  <td>{o.origin_channel}</td>
                  <td>{o.attributed_rep_id || '—'}</td>
                  <td>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td>
                    {o.customer_id ? (
                      <a
                        href={`https://pro.housecallpro.com/app/customers/${o.customer_id}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Open in HouseCallPro"
                      >
                        HCP Link
                      </a>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
